import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAllSearchNewsData } from '../../Services/AllAPIs';
import '../../styles/search-news/main.css';

/* -----------------------------------------------------
   Decode HTML entities like &#2325; into actual letters
----------------------------------------------------- */
function decodeHtmlEntities(text = '') {
  const txt = document.createElement('textarea');
  txt.innerHTML = text;
  return txt.value;
}

const SearchNews = () => {
  /* -------- URL ↔ state -------- */
  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    title: searchParams.get('title') || '',
    category: searchParams.get('category') || '',
    month: searchParams.get('month') || '',
    year: searchParams.get('year') || '',
  });

  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* -------- fetch on mount + every filter/page change -------- */
  useEffect(() => {
    const controller = new AbortController();

    const fetchNews = async () => {
      setLoading(true);
      setError(null);

      const params = {
        title: formData.title,
        id: formData.category, // backend param is id=<category-name>
        month: formData.month,
        year: formData.year,
        page,
      };

      try {
        const { data } = await getAllSearchNewsData(params, {
          signal: controller.signal,
        });

        setNews(Array.isArray(data.news) ? data.news : Object.values(data.news));
        setTotalPages(data.totalPages || 1);
        const catArr = Array.isArray(data.categories)
          ? data.categories
          : Object.values(data.categories || {});
        setCategories(catArr);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error(err);
          setError('Unable to fetch news.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchNews();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, page]);

  /* -------- handlers -------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Immediately refresh when category changes
    if (name === 'category') {
      setPage(1);
      setSearchParams({ ...formData, [name]: value, page: 1 });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchParams({ ...formData, page: 1 });
  };

  const handlePagination = (direction) => {
    const newPage = direction === 'next' ? page + 1 : page - 1;
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      setSearchParams({ ...formData, page: newPage });
    }
  };

  // const formatDate = (dateStr) =>
  //   dateStr
  //     ? new Date(dateStr).toLocaleDateString('en-GB', {
  //         day: '2-digit',
  //         month: 'short',
  //         year: 'numeric',
  //       })
  //     : 'N/A';

//Modify the date format
  const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  
  const date = new Date(dateStr);
  
  // Subtract IST offset (5:30 hours) since the database stores IST as UTC-naive
  const istOffset = 5.5 * 60 * 60 * 1000; // 5:30 hours in milliseconds
  const correctedDate = new Date(date.getTime() - istOffset);
  
  return correctedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};
 

  /* -------- JSX (UI unchanged, with decoding) -------- */
  return (
    <div id="searchNewsMain">
      <section className="news-main-div">
        <div className="container">
          <div>
            <h1 className="my-heading">
              {formData.category
                ? `${decodeHtmlEntities(formData.category)} News & Updates`
                : 'News & Updates'}
            </h1>
          </div>

          <div className="row p-0 m-0 d-flex align-items-start justify-content-between">
            {/* --------------- Filters --------------- */}
            <div className="col-lg-3">
              <form onSubmit={handleSubmit} method="GET">
                <div>
                  <label>Title</label>
                  <input
                    className="form-control"
                    type="text"
                    name="title"
                    placeholder="Enter Search Value"
                    value={formData.title}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="mt-2">
                  <label>Category</label>
                  <select
                    name="category"
                    id="category"
                    className="form-control"
                    value={formData.category}
                    onChange={handleInputChange}
                  >
                    <option value="">--Select Category--</option>
                    {categories.length ? (
                      categories.map((cat) => (
                        <option key={cat.id || cat.name} value={cat.name}>
                          {decodeHtmlEntities(cat.name)}
                        </option>
                      ))
                    ) : (
                      <option value="">No categories available</option>
                    )}
                  </select>
                </div>

                <div className="mt-2">
                  <label>Month</label>
                  <select
                    name="month"
                    id="month"
                    className="form-control"
                    value={formData.month}
                    onChange={handleInputChange}
                  >
                    <option value="">--Select Month--</option>
                    {[...Array(12).keys()].map((i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString('default', { month: 'short' })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-2">
                  <label>Year</label>
                  <select
                    name="year"
                    id="year"
                    className="form-control"
                    value={formData.year}
                    onChange={handleInputChange}
                  >
                    <option value="">--Select Year--</option>
                    {[2025, 2024, 2023, 2022, 2021, 2020].map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="btns-grp mt-2">
                  <button type="submit">Search</button>
                  <button
                    type="reset"
                    onClick={() => {
                      setFormData({ title: '', category: '', month: '', year: '' });
                      setPage(1);
                      setSearchParams({});
                    }}
                  >
                    Reset
                  </button>
                </div>
              </form>
            </div>

            {/* --------------- Table --------------- */}
            <div className="col-lg-8 table-div">
              <div>
                <table>
                  <thead>
                    <tr>
                      <th style={{ borderRight: '1px solid #FFF', textAlign: 'center' }}>Date</th>
                      <th style={{ borderRight: '1px solid #FFF' }}>Title</th>
                      <th className="text-center">View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="3" className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="3" className="text-center">
                          {error}
                        </td>
                      </tr>
                    ) : news.length ? (
                      news.map((item) => (
                        <tr key={item.id}>
                          <td className="date-data">{formatDate(item.created_at)}</td>
                          <td>
                            <a href={item.url} target="_blank" rel="noopener noreferrer">
                              {decodeHtmlEntities(item.title)}
                            </a>
                          </td>
                          <td>
                            <button>
                              <a href={item.url} target="_blank" rel="noopener noreferrer">
                                View
                              </a>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">
                          No results found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <br />

                {/* ------------- Pagination ------------- */}
                <div className="pagination">
                  <button
                    className="btn btn-primary"
                    onClick={() => handlePagination('previous')}
                    disabled={page <= 1}
                    style={{ backgroundColor: '#14297a' }}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => handlePagination('next')}
                    disabled={page >= totalPages}
                    style={{ backgroundColor: '#14297a', marginLeft: 10 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SearchNews;
